import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { Sensor } from "@station/domain/models/sensor.model";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const YAxesSchema = z
    .object({
        id: z.number(),
        label: z.string().trim(),
        variableIds: z.array(z.number()),
    })
    .readonly();
export type YAxes = z.output<typeof YAxesSchema>;

const SensorGroupSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        publicName: z.string().trim().nullable(),
        sensors: z.array(
            z.custom<Sensor>((val) => val instanceof Sensor, {
                error: "Invalid Sensor instance",
            }),
        ),
        yAxes: z.array(YAxesSchema),
    })
    .brand<"SensorGroup">();

export type SensorGroupInputProps = z.input<typeof SensorGroupSchema>;
type SensorGroupProps = z.output<typeof SensorGroupSchema>;

export class SensorGroup {
    private constructor(private readonly props: Readonly<SensorGroupProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get publicName(): Nullable<string> {
        return this.props.publicName;
    }

    get sensors(): readonly Readonly<Sensor>[] {
        return this.props.sensors;
    }

    get yAxes(): readonly YAxes[] {
        return this.props.yAxes;
    }

    static validate(
        props: Readonly<MaybeValidProps<SensorGroupInputProps>>,
    ): Result<SensorGroupProps, z.ZodError<SensorGroupProps>> {
        const result = SensorGroupSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<SensorGroupInputProps>>,
    ): Result<SensorGroupProps, PartialValid<typeof SensorGroup>> {
        const result = partialSafeParse(SensorGroupSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<SensorGroupInputProps>,
    ): Result<SensorGroup, z.ZodError<SensorGroupProps>> {
        return SensorGroup.validate(props).map(
            (validatedProps) => new SensorGroup(validatedProps),
        );
    }

    static createWithRecovery(
        props: SensorGroupInputProps,
    ): Result<SensorGroup, ModelError<typeof SensorGroup>> {
        return SensorGroup.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<SensorGroupProps> = {
                    id: error.validProps.id,
                    name: "",
                    publicName: "",
                    sensors: [],
                    yAxes: [],
                };

                const recoveryProps: Unbrand<SensorGroupProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof SensorGroup>(
                    new SensorGroup(recoveryProps as SensorGroupProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new SensorGroup(validatedProps));
    }

    toCreateProps(): SensorGroupInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            publicName: this.props.publicName,
            sensors: this.props.sensors,
            yAxes: this.props.yAxes,
        };
    }
}

const SensorGroupsSchema = z
    .object({
        sensorGroups: z.array(
            z.custom<SensorGroup>((val) => val instanceof SensorGroup, {
                error: "Invalid SensorGroup instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"SensorGroups">();

export type SensorGroupsInputProps = z.input<typeof SensorGroupsSchema>;
export type SensorGroupsProps = z.output<typeof SensorGroupsSchema>;

export class SensorGroups {
    private constructor(private readonly props: Readonly<SensorGroupsProps>) {}

    get sensorGroups(): readonly Readonly<SensorGroup>[] {
        return this.props.sensorGroups;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): SensorGroups {
        return SensorGroups.create({
            sensorGroups: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SensorGroupsInputProps>>,
    ): Result<SensorGroupsProps, z.ZodError<SensorGroupsProps>> {
        const result = SensorGroupsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<SensorGroupsInputProps>,
    ): Result<SensorGroups, z.ZodError<SensorGroupsProps>> {
        return SensorGroups.validate(props).map(
            (validatedProps) => new SensorGroups(validatedProps),
        );
    }
}
